import React, { useState, useEffect, useRef } from 'react';
import '@googleworkspace/drive-picker-element';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../../store';
import { setRecentlyGrantedAccess, clearRecentlyGrantedAccess } from '../../../lib/redux/sheets/slice';

// Module augmentation for JSX intrinsic elements
declare module 'react' {
  namespace JSX {
    interface IntrinsicElements {
      'drive-picker': {
        'client-id'?: string;
        'app-id'?: string;
        'oauth-token'?: string;
        'developer-key'?: string;
        multiselect?: boolean;
        origin?: string;
        visible?: boolean;
        ref?: React.MutableRefObject<any>;
        children?: React.ReactNode;
      };
      'drive-picker-docs-view': {
        'view-id'?: string;
        'mime-types'?: string;
        'file-ids'?: string;
        query?: string;
      };
    }
  }
}

const GOOGLE_APP_ID = '50226627522';
const GOOGLE_CLIENT_ID = '50226627522-ekkero1qdgs8glk3t809ice0ld8h4i2n.apps.googleusercontent.com';
const GOOGLE_API_KEY = 'AIzaSyBKvy73tCI8SLsLmZs_PP73tJSgkRDG35s';

export function FileAccessNeeded() {
  const auth = useSelector((state: RootState) => state.googleSheets.auth);
  const spreadsheetId = useSelector((state: RootState) => state.googleSheets.windowMessages.latestSpreadsheetIdRequested);
  const recentlyGrantedAccess = useSelector((state: RootState) => state.googleSheets.windowMessages.recentlyGrantedAccess);
  const spreadsheetAccess = useSelector((state: RootState) => state.googleSheets.spreadsheetData.spreadsheetAccess);
  const dispatch = useDispatch();
  const [showPickerComponent, setShowPickerComponent] = useState<boolean>(false);
  const [componentError, setComponentError] = useState<string | null>(null);

  const getPickerOrigin = () => {
    if (window.parent && window.parent !== window && document.location.ancestorOrigins && document.location.ancestorOrigins.length > 0) {
      return document.location.ancestorOrigins[document.location.ancestorOrigins.length - 1];
    }
    return window.location.protocol + '//' + window.location.host;
  };

  const handlePickerCancel = (event: any) => {
    setShowPickerComponent(false);
  };

  // Handle picker events
  const handlePickerPicked = (event: any) => {
    setShowPickerComponent(false);
    const docs = event.detail.docs;
    if (docs && docs.length > 0) {
      const doc = docs[0];
      const selectedId = doc.id;
      setComponentError(null);
      dispatch(setRecentlyGrantedAccess({
        spreadsheetId: selectedId,
        grantedAt: Date.now(),
        retryAttempt: 0,
        isRetrying: true,
      }))
    }
  }

  const drivePickerRef = useRef<any>(null);

  // Set up event listeners for the drive-picker component
  useEffect(() => {
    if (drivePickerRef.current) {
      const pickerElement = drivePickerRef.current;
      
      pickerElement.addEventListener('picker-picked', handlePickerPicked);
      pickerElement.addEventListener('picker-canceled', handlePickerCancel);
      
      return () => {
        pickerElement.removeEventListener('picker-picked', handlePickerPicked);
        pickerElement.removeEventListener('picker-canceled', handlePickerCancel);
      };
    }
  }, [showPickerComponent, auth.tokenConfig]);
  const hasSuccessfulAccess = recentlyGrantedAccess && spreadsheetAccess.includes(recentlyGrantedAccess.spreadsheetId)
  if (!spreadsheetId) {
    return (
      <div className="text-center space-y-4">
        <h3 className="text-lg font-medium text-gray-900">Access Needed</h3>
        <p className="text-sm text-gray-600">Navigate to a spreadsheet and open the Trellus icon from the top right</p>
      </div>
    )
  }

  // Show success state if access was granted successfully
  if (hasSuccessfulAccess) {
    return (
      <div className="text-center space-y-4">
        <div className="text-green-600">
          <svg className="mx-auto h-12 w-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-gray-900">Access Granted!</h3>
        <p className="text-sm text-gray-600">
          Successfully connected to your spreadsheet. Setting up columns...
        </p>
      </div>
    );
  }

  // Show retry state if we're in the process of checking access
  if (recentlyGrantedAccess && recentlyGrantedAccess.isRetrying) {
    const { retryAttempt, lastError } = recentlyGrantedAccess;
    return (
      <div className="text-center space-y-4">
        <div className="text-blue-600">
          <svg className="animate-spin mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        </div>
        <h3 className="text-lg font-medium text-gray-900">Checking Access...</h3>
        <p className="text-sm text-gray-600">
          Verifying spreadsheet permissions (attempt {retryAttempt + 1}/4)
        </p>
        {lastError && (
          <p className="text-xs text-red-600 mt-2">
            {lastError}
          </p>
        )}
      </div>
    );
  }

  // Show error state if we failed after all retries
  if (recentlyGrantedAccess && !recentlyGrantedAccess.isRetrying && recentlyGrantedAccess.lastError) {
    return (
      <div className="text-center space-y-4">
        <div className="text-red-600">
          <svg className="mx-auto h-12 w-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-gray-900">Access Failed</h3>
        <p className="text-sm text-gray-600">
          Unable to verify access to the selected spreadsheet after multiple attempts.
        </p>
        <button 
          onClick={() => {
            dispatch(clearRecentlyGrantedAccess());
            setShowPickerComponent(true);
          }}
          className="text-sm text-blue-600 hover:text-blue-700 underline"
        >
          Try Another Spreadsheet
        </button>
      </div>
    );
  }

  // Default state - show picker option
  return (
    <div className="space-y-4">
      {componentError && <div className="text-center text-red-500">{componentError}</div>}
      <div className="text-center">
        <h3 className="text-lg font-medium text-gray-900 mb-2">Share Spreadsheet</h3>
        <p className="text-sm text-gray-600 mb-4">
          Click below to select and share your spreadsheet with Trellus
        </p>
        <button onClick={() => setShowPickerComponent(true)} className="text-sm text-gray-600 hover:text-gray-700 underline">
          Select Spreadsheet
        </button>
      </div>
      
      {showPickerComponent && auth.tokenConfig ? (
        // @ts-ignore
        <drive-picker
          ref={drivePickerRef}
          client-id={GOOGLE_CLIENT_ID}
          app-id={GOOGLE_APP_ID}
          oauth-token={auth.tokenConfig.access_token}
          developer-key={GOOGLE_API_KEY}
          multiselect={false}
          origin={getPickerOrigin()}
          visible={true}
        >
          {/* @ts-ignore */}
          <drive-picker-docs-view
            enable-drives="true"
            view-id="SPREADSHEETS"
            mime-types="application/vnd.google-apps.spreadsheet"
            file-ids={spreadsheetId.toString()}
          />
        </drive-picker>
      ) : null}
    </div>
  );
} 